import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useHandleErrors from '../../hooks/useHandleErrors';
import {
    Alert,
    Box,
    Button,
    Chip,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useFetch } from '../../hooks/useFetch';
import LoadingContent, { ErrorContent } from '../../components/LoadingContent';
import { getServerNow, useServerTime } from '../../hooks/useServerTime';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CasinoIcon from '@mui/icons-material/Casino';
import AddIcon from '@mui/icons-material/Add';
import server from '../../global/server';
import { useSnackbar } from 'notistack';
import { useConfirm } from 'material-ui-confirm';
import RaffleWheel from './components/RaffleWheel';

const SectionCard = ({ children, onClick, sx }) => (
    <Box
        onClick={onClick}
        sx={{
            width: '100%',
            bgcolor: 'white',
            borderRadius: '16px',
            border: '1px solid #e8e8e8',
            p: { xs: '16px', sm: '20px 24px' },
            cursor: onClick ? 'pointer' : 'default',
            transition: 'all 0.2s ease',
            ...(onClick && {
                '&:hover': {
                    borderColor: '#006a61',
                    boxShadow: '0 2px 12px rgba(0,106,97,0.08)',
                },
            }),
            ...sx,
        }}
    >
        {children}
    </Box>
);

// Horario que ainda conta como aberto: nao finalizado e dentro da janela de tempo
const isSchedulePending = (schedule, now, oneHourMs) =>
    Boolean(schedule.datetime) &&
    !schedule.finished_at &&
    new Date(schedule.datetime) > new Date(now.getTime() - oneHourMs);

const MeetingItem = ({ meeting, onRaffle }) => {
    const navigation = useNavigate();
    const now = getServerNow();

    const schedules = (meeting.meeting_schedule || []).filter(s => s.datetime);
    // Horario finalizado nao volta a ser o "proximo", mesmo dentro da janela de tempo
    const futureSchedules = schedules.filter(
        s => !s.finished_at && new Date(s.datetime) > new Date(now.getTime() - 15 * 60 * 1000)
    );
    const nextSchedule = futureSchedules.sort((a, b) => new Date(a.datetime) - new Date(b.datetime))[0];
    const totalOrders = schedules.reduce((acc, s) => {
        return acc + (s.Order ? 1 : 0);
    }, 0);
    const hasSchedules = schedules.length > 0;

    // Verificar se tem sorteio pendente (encontro de sorteio com ordens não sorteadas)
    const isRaffle = meeting.raffle;
    const pendingRaffleSchedules = isRaffle ? schedules.filter(s => {
        const orders = Array.isArray(s.Order) ? s.Order : [];
        return orders.length > 0 && orders.some(o => !o.chosen);
    }) : [];
    const hasPendingRaffle = pendingRaffleSchedules.length > 0;

    // Status do próximo horário
    let statusChip = null;
    if (!hasSchedules) {
        statusChip = <Chip icon={<WarningAmberIcon sx={{ fontSize: 14 }} />} label="Sem horários" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 500, fontSize: '0.7rem' }} />;
    } else if (nextSchedule) {
        const diff = (now.getTime() - new Date(nextSchedule.datetime).getTime()) / 1000;
        const hasOrder = Array.isArray(nextSchedule.Order) && nextSchedule.Order.length > 0;
        if (diff >= 0 && diff <= 3600 && hasOrder) {
            statusChip = <Chip label="Ao vivo" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }} />;
        } else if (hasOrder) {
            statusChip = <Chip label="Confirmado" size="small" sx={{ bgcolor: '#e6f3f1', color: '#006a61', fontWeight: 500, fontSize: '0.7rem' }} />;
        } else {
            statusChip = <Chip label="Disponível" size="small" sx={{ bgcolor: '#f5f5f5', color: '#888', fontWeight: 500, fontSize: '0.7rem' }} />;
        }
    } else {
        statusChip = <Chip label="Encerrado" size="small" sx={{ bgcolor: '#f5f5f5', color: '#999', fontSize: '0.7rem' }} />;
    }

    const nextDateFormatted = nextSchedule
        ? new Date(nextSchedule.datetime).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
        : null;
    const nextTimeFormatted = nextSchedule
        ? new Date(nextSchedule.datetime).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        : null;

    return (
        <SectionCard onClick={() => navigation(`/meus-encontros/${meeting.id}/horarios`)}>
            {/* Header: titulo + status */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                        {meeting.title}
                    </Typography>
                    {/* Tags de assunto */}
                    {(meeting.topic || meeting.subtopic) && (
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
                            {meeting.topic && <Chip label={meeting.topic} size="small" sx={{ height: 20, fontSize: '0.6rem', bgcolor: '#e6f3f1', color: '#006a61' }} />}
                            {meeting.subtopic && <Chip label={meeting.subtopic} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.6rem', borderColor: '#ccc', color: '#888' }} />}
                        </Stack>
                    )}
                </Box>
                <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
                    {statusChip}
                    <Stack direction="row" spacing={0.5}>
                        {meeting.free
                            ? <Chip label="Gratuito" size="small" sx={{ height: 20, fontSize: '0.6rem', bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                            : meeting.pricing > 0 && <Chip label={`R$ ${Number(meeting.pricing).toFixed(2).replace('.', ',')}`} size="small" sx={{ height: 20, fontSize: '0.6rem', bgcolor: '#fef3c7', color: '#b45309' }} />
                        }
                        <Chip label={meeting.raffle ? 'Sorteio' : 'Agendamento'} size="small" sx={{ height: 20, fontSize: '0.6rem', bgcolor: '#f5f5f5', color: '#888' }} />
                    </Stack>
                </Stack>
            </Stack>

            {/* Descricao */}
            {meeting.description && (
                <Typography variant="body2" color="text.secondary" sx={{
                    mt: 1,
                    fontSize: '0.8rem',
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }}>
                    {meeting.description}
                </Typography>
            )}

            {/* Horarios */}
            {futureSchedules.length > 0 && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #f0f0f0' }}>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                        {futureSchedules.length} horario{futureSchedules.length > 1 ? 's' : ''}{!isRaffle ? ` · ${totalOrders} conviva${totalOrders !== 1 ? 's' : ''}` : ''}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                        {futureSchedules.slice(0, 5).map((s) => {
                            const dt = new Date(s.datetime);
                            const day = dt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
                            const time = dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                            const hasOrder = Boolean(s.Order);
                            return (
                                <Stack key={s.id} direction="row" alignItems="center" spacing={0.3}
                                    sx={{
                                        bgcolor: hasOrder ? '#e6f3f1' : '#f5f5f5',
                                        borderRadius: '6px',
                                        px: 0.8,
                                        py: 0.3,
                                        border: hasOrder ? '1px solid #006a61' : '1px solid transparent',
                                    }}
                                >
                                    <AccessTimeIcon sx={{ fontSize: 11, color: hasOrder ? '#006a61' : '#999' }} />
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: hasOrder ? '#006a61' : '#666', fontWeight: hasOrder ? 600 : 400, whiteSpace: 'nowrap' }}>
                                        {day} {time}
                                    </Typography>
                                </Stack>
                            );
                        })}
                        {futureSchedules.length > 5 && (
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#006a61', fontWeight: 500, alignSelf: 'center' }}>
                                +{futureSchedules.length - 5} mais
                            </Typography>
                        )}
                    </Stack>
                </Box>
            )}

            {/* Botão de sorteio */}
            {hasPendingRaffle && (
                <Button
                    startIcon={<CasinoIcon sx={{ fontSize: 16 }} />}
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRaffle(meeting.id);
                    }}
                    sx={{
                        mt: 1.5,
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#7b1fa2',
                        bgcolor: '#f3e5f5',
                        borderRadius: '8px',
                        px: 2,
                        '&:hover': { bgcolor: '#e1bee7' },
                    }}
                >
                    Realizar sorteio ({pendingRaffleSchedules.length} horario{pendingRaffleSchedules.length > 1 ? 's' : ''})
                </Button>
            )}
        </SectionCard>
    );
};

export default () => {
    useServerTime();
    const { user } = useAuth();
    const [hasError, setError] = useState();
    useHandleErrors(hasError);
    const navigation = useNavigate();
    const { data, error, mutate, isLoading } = useFetch('/profile/meeting');
    useHandleErrors(error);
    const { enqueueSnackbar } = useSnackbar();
    const confirm = useConfirm();

    // Estado da roleta
    const [raffleOpen, setRaffleOpen] = useState(false);
    const [raffleData, setRaffleData] = useState(null); // { participants, scheduleData }
    const [loadingRaffle, setLoadingRaffle] = useState(false);

    const openRaffle = async (meetingId) => {
        setLoadingRaffle(true);
        try {
            const { data: res } = await server.get(`/profile/schedule/raffle/${meetingId}`);
            const slots = res.data;
            if (!slots || slots.length === 0) {
                enqueueSnackbar('Nenhum horário pendente de sorteio', { variant: 'info' });
                return;
            }
            // Se tem múltiplos horários, usar o primeiro pendente
            const slot = slots[0];
            if (!slot.participants || slot.participants.length === 0) {
                enqueueSnackbar('Nenhum participante para sortear', { variant: 'info' });
                return;
            }
            setRaffleData({
                participants: slot.participants,
                scheduleData: {
                    meeting_id: meetingId,
                    datetime: slot.datetime,
                    meeting_scheduled_id: slot.meeting_scheduled_id,
                },
            });
            setRaffleOpen(true);
        } catch (err) {
            setError(err);
        } finally {
            setLoadingRaffle(false);
        }
    };

    if (isLoading) return <LoadingContent />;
    if (error) return <ErrorContent label="Ocorreu um erro. Tente novamente mais tarde." />;

    const now = getServerNow();
    const oneHourMs = 60 * 60 * 1000;

    // Encontro "acontecendo agora": tem horário que começou há menos de 1h, tem
    // order e ainda nao foi finalizado
    const happeningNow = (data || []).filter(m => {
        const schedules = m.meeting_schedule || [];
        return schedules.some(s => {
            if (!s.datetime || s.finished_at) return false;
            const dt = new Date(s.datetime);
            const diff = now.getTime() - dt.getTime();
            const hasOrder = s.Order ? (Array.isArray(s.Order) ? s.Order.length > 0 : Boolean(s.Order)) : false;
            return diff >= 0 && diff <= oneHourMs && hasOrder;
        });
    });
    const happeningNowIds = happeningNow.map(m => m.id);

    // Próximos: tem horários futuros ou acontecendo (mas não na lista de "acontecendo agora" para evitar duplicata)
    const withFutureSchedules = (data || []).filter(m => {
        if (happeningNowIds.includes(m.id)) return false;
        const schedules = m.meeting_schedule || [];
        return schedules.some(s => isSchedulePending(s, now, oneHourMs));
    });

    // Encerrados / sem horários
    const withoutFutureSchedules = (data || []).filter(m => {
        if (happeningNowIds.includes(m.id)) return false;
        const schedules = m.meeting_schedule || [];
        if (schedules.length === 0) return true;
        return !schedules.some(s => isSchedulePending(s, now, oneHourMs));
    });

    withFutureSchedules.sort((a, b) => {
        const nextA = (a.meeting_schedule || []).filter(s => s.datetime).sort((x, y) => new Date(x.datetime) - new Date(y.datetime))[0];
        const nextB = (b.meeting_schedule || []).filter(s => s.datetime).sort((x, y) => new Date(x.datetime) - new Date(y.datetime))[0];
        if (!nextA) return 1;
        if (!nextB) return -1;
        return new Date(nextA.datetime) - new Date(nextB.datetime);
    });

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 640,
            mx: 'auto',
            p: { xs: '16px 12px 32px', sm: '24px 16px 48px' },
            gap: 2,
        }}>
            <Typography variant="h6" fontWeight={600} sx={{ alignSelf: 'flex-start' }}>
                Encontros criados
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'flex-start', mt: -1 }}>
                Encontros que você criou como anfitrião.
            </Typography>

            {user?.profile && !user.profile.approved && (
                <Alert severity="warning" sx={{ width: '100%', borderRadius: '12px', '& .MuiAlert-message': { fontSize: '0.85rem' } }}>
                    Seu cadastro de anfitrião está em análise. Seus encontros só ficarão visíveis para outros usuários após a aprovação.
                </Alert>
            )}

            {happeningNow.length > 0 && (
                <>
                    <Typography variant="subtitle2" fontWeight={600} color="#2e7d32" sx={{ alignSelf: 'flex-start', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                        Acontecendo agora ({happeningNow.length})
                    </Typography>
                    <Stack spacing={1.5} sx={{ width: '100%' }}>
                        {happeningNow.map((meeting) => (
                            <MeetingItem
                                key={meeting.id}
                                meeting={meeting}
                                onRaffle={openRaffle}
                            />
                        ))}
                    </Stack>
                </>
            )}

            {withFutureSchedules.length > 0 && (
                <>
                    {happeningNow.length > 0 && (
                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ alignSelf: 'flex-start', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem', mt: 1 }}>
                            Próximos ({withFutureSchedules.length})
                        </Typography>
                    )}
                    <Stack spacing={1.5} sx={{ width: '100%' }}>
                        {withFutureSchedules.map((meeting) => (
                            <MeetingItem
                                key={meeting.id}
                                meeting={meeting}
                                onRaffle={openRaffle}
                            />
                        ))}
                    </Stack>
                </>
            )}

            {withoutFutureSchedules.length > 0 && (
                <>
                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ alignSelf: 'flex-start', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem', mt: 1 }}>
                        Encerrados / Sem horarios ({withoutFutureSchedules.length})
                    </Typography>
                    <Stack spacing={1.5} sx={{ width: '100%' }}>
                        {withoutFutureSchedules.map((meeting) => (
                            <SectionCard key={meeting.id} sx={{ opacity: 0.6 }} onClick={() => navigation(`/meus-encontros/${meeting.id}/horarios`)}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="subtitle2" fontWeight={500} noWrap color="text.secondary">
                                            {meeting.title}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={(meeting.meeting_schedule || []).length === 0 ? 'Sem horários' : 'Encerrado'}
                                        size="small"
                                        sx={{ bgcolor: '#f5f5f5', color: '#999', fontSize: '0.7rem' }}
                                    />
                                    <Tooltip title="Recriar encontro" arrow>
                                        <IconButton
                                            size="small"
                                            // stopPropagation: o card inteiro navega para os horarios
                                            onClick={e => {
                                                e.stopPropagation();
                                                navigation(`/meus-encontros/novo?recriar=${meeting.id}`);
                                            }}
                                            sx={{
                                                color: '#2e7d32',
                                                flexShrink: 0,
                                                '&:hover': { bgcolor: 'transparent', color: '#1b5e20' },
                                            }}
                                        >
                                            <AddIcon sx={{ fontSize: 22 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <ChevronRightIcon sx={{ color: '#ccc', fontSize: 20, flexShrink: 0 }} />
                                </Stack>
                            </SectionCard>
                        ))}
                    </Stack>
                </>
            )}

            {(!data || data.length === 0) && (
                <>
                    <Alert severity="info" sx={{ width: '100%', borderRadius: '12px' }}>
                        Você ainda não criou nenhum encontro.
                    </Alert>
                    <Button
                        variant="contained"
                        onClick={() => navigation('/meus-encontros/novo')}
                        sx={{
                            bgcolor: '#006a61',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#005a52' },
                        }}
                    >
                        Criar meu primeiro encontro
                    </Button>
                </>
            )}

            {/* Roleta de sorteio */}
            {raffleData && (
                <RaffleWheel
                    open={raffleOpen}
                    onClose={() => { setRaffleOpen(false); setRaffleData(null); }}
                    participants={raffleData.participants}
                    scheduleData={raffleData.scheduleData}
                    mutate={mutate}
                />
            )}
        </Box>
    );
};
